import { Subscription } from "./subscription";

// Publics methods
export const handleAddSubscription = (state, action) => {
	const subscriptions = addSubscription(state.subscriptions, action);
	return {
		...state,
		subscriptions,
	};
};

export const handleAddSubscriptions = (state, action) => {
	let subscriptions = state.subscriptions;

	if (action.subscriptionsList?.length) {
		action.subscriptionsList?.forEach((element) => {
			subscriptions = addSubscription(subscriptions, {
				identifier: element.subscriptionKey,
				subscriptions: element.subscriptions,
				persistent: element.persistent,
				maxSubscriptions: action.maxSubscriptions,
				queryClient: action.queryClient || null,
			});
		});
	}

	return {
		...state,
		subscriptions,
	};
};

const addSubscription = (subscriptionsMap, parameters) => {
	const { queryClient, identifier, subscriptions, persistent, maxSubscriptions } = parameters;
	const key = getMapKey(identifier);

	if (!subscriptionsMap.has(key)) {
		const subscription = instanceSubscription(identifier, subscriptions || [], queryClient, persistent);

		if (haveSizeToAllocate(subscriptionsMap, maxSubscriptions, subscription.getSize())) {
			return subscriptionsMap.set(key, subscription);
		}

		if (!isAllocationPermitted(subscriptionsMap, maxSubscriptions, subscription.getSize())) {
			console.error("Can not allocate new subscriptions. Subscription size exceeded.");
			return subscriptionsMap;
		}

		removeSubscriptionsOnDemand(subscriptionsMap, subscription.getSize());
		return subscriptionsMap.set(key, subscription);
	}

	return subscriptionsMap;
};

export const handleDeleteSubscription = (state, action) => {
	const key = getMapKey(action.identifier);

	if (state?.subscriptions?.has(key)) {
		const subscription = state.subscriptions.get(key);

		if (!subscription.isPersistent()) {
			subscription.clean();
			state.subscriptions.delete(key);
		}
	}

	return { ...state };
};

export const handleClearSubscriptionContext = (state) => {
	const subscriptions = state?.subscriptions;

	if (subscriptions?.size) {
		for (const [key, subscription] of subscriptions) {
			if (subscription && !subscription.isPersistent()) {
				subscription.clean();
				subscriptions.delete(key);
			}
		}
	}

	return { subscriptions };
};

export const handleClearSubscriptions = (state) => {
	if (state?.subscriptions?.size) {
		for (const [key, subscription] of state.subscriptions) {
			if (subscription) {
				subscription.clean();
			}
			state.subscriptions.delete(key);
		}
	}

	return { subscriptions: new Map() };
};

export const handleGetSubscription = (state, identifier) => {
	const key = getMapKey(identifier);
	if (state?.subscriptions.has(key)) {
		return state.subscriptions.get(key);
	}
	return null;
};

// Private methods
const getMapKey = (list = []) => list.join("_|_");

const instanceSubscription = (...params) => {
	const [identifier, subscriptionsList, queryClient = null, persistent = false] = params;
	const subscription = new Subscription(identifier, getMapKey(identifier), queryClient, persistent);
	subscription.addSubscriptionsList(subscriptionsList);
	return subscription;
};

const haveSizeToAllocate = (subscriptions, maxSubscriptions, newSubscriptionsSize) => {
	const totalAllocated = getAllocatedSize(subscriptions);
	return Number(totalAllocated + newSubscriptionsSize) < Number(maxSubscriptions);
};

const isAllocationPermitted = (subscriptions, maxSubscriptions, newSubscriptionsSize) => {
	const totalPersistentAllocated = getAllocatedSize(subscriptions, "persistent");
	const canAllocate = Number(maxSubscriptions - totalPersistentAllocated);
	return Number(newSubscriptionsSize) < Number(canAllocate);
};

const compareTimestamps = (a, b) => {
	if (a[1].timestamp > b[1].timestamp) {
		return 1;
	}
	if (a[1].timestamp < b[1].timestamp) {
		return -1;
	}
	return 0;
};

const removeSubscriptionsOnDemand = (subscriptions, newSubscriptionsSize) => {
	let totalToReduce = 0;

	const cloneSubscriptions = new Map([...subscriptions].sort(compareTimestamps));

	for (const [key, element] of cloneSubscriptions) {
		if (element.persistent) {
			cloneSubscriptions.delete(key);
		}
	}

	while (totalToReduce < newSubscriptionsSize) {
		const subscription = cloneSubscriptions.entries().next().value[1];
		if (subscription) {
			totalToReduce += subscription.getSize();
			cloneSubscriptions.delete(subscription.index);

			if (subscriptions?.has(subscription.index)) {
				const localSubscription = subscriptions.get(subscription.index);
				localSubscription.clean();
				subscriptions.delete(subscription.index);
			}
		}
	}

	return { ...subscriptions };
};

const getAllocatedSize = (subscriptions, type = "total") => {
	let total = 0;

	if (type === "persistent") {
		subscriptions.forEach((subscription) => {
			total += subscription.getPersistentSize() || 0;
		});
	} else {
		subscriptions.forEach((subscription) => {
			total += subscription.getSize() || 0;
		});
	}

	return Number(total);
};
